import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { isValidContactFormVariant } from "../../types/contactFormVariants.ts";
import { CallMeBackModal, type CallMeBackModalHandle } from "../components/modals/CallMeBackModal.tsx";
import { ContactFormModal, type ContactFormModalHandle } from "../components/modals/ContactFormModal.tsx";
import { ContactFormModalv2, type ContactFormModalv2Handle } from "../components/modals/ContactFormModalv2.tsx";
import { ContactFormModalv3, type ContactFormModalv3Handle } from "../components/modals/ContactFormModalv3.tsx";
import { EidModal, type EidModalHandle } from "../components/modals/EidModal.tsx";
import { LoginModal, type LoginModalHandle } from "../components/modals/LoginModal.tsx";
import { ReserveCarInfoModal, type ReserveCarInfoModalHandle } from "../components/modals/ReserveCarInfoModal.tsx";
import { ReserveCarModal, type ReserveCarModalHandle } from "../components/modals/ReserveCarModal.tsx";
import type { PrefilledMessage } from "../utils/prefilledMessage.ts";
import { useTracking } from "../utils/pulseHelper.ts";
import { useSafeDealerItemContext, useValidDealerItemContext } from "./DealerItemProviderContext.tsx";

type OpenContactFlowOptions = {
  prefilledMessage?: PrefilledMessage;
  /**
   * Slug identifying the topic. eID takes the user off the page, so it travels along as
   * `?contact=<topic>` and the caller can hand the prefilled message back when they land on the ad again.
   */
  contactTopic?: string;
};

type ModalContextType = {
  openLoginModal: () => void;
  openContactFlow: (options?: OpenContactFlowOptions) => void;
  openCallMeBackFlow: () => void;
  openReserveCarModal: () => void;
  openReserveCarInfoModal: () => void;
  isReserved: boolean;
  setIsReserved: Dispatch<SetStateAction<boolean>>;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

const noopModal = () => {};

/**
 * Provides modal open functions without rendering actual modals.
 * Useful for tests that don't need modal behavior.
 */
export function MockModalProvider({ children }: { children: ReactNode }) {
  const [isReserved, setIsReserved] = useState(false);
  return (
    <ModalContext
      value={{
        openLoginModal: noopModal,
        openContactFlow: noopModal,
        openCallMeBackFlow: noopModal,
        openReserveCarModal: noopModal,
        openReserveCarInfoModal: noopModal,
        isReserved,
        setIsReserved,
      }}
    >
      {children}
    </ModalContext>
  );
}

/**
 * ModalProvider that safely checks for dealer data availability.
 * If dealer data is not available, provides noop modal functions.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  const dealerItemData = useSafeDealerItemContext();

  if (!dealerItemData) {
    return <MockModalProvider>{children}</MockModalProvider>;
  }

  return <ModalProviderInner>{children}</ModalProviderInner>;
}

/**
 * Inner provider that renders modals when dealer data is guaranteed to be available.
 */
function ModalProviderInner({ children }: { children: ReactNode }) {
  const loginModalRef = useRef<LoginModalHandle>(null);
  const eidModalRef = useRef<EidModalHandle>(null);
  const contactFormV1Ref = useRef<ContactFormModalHandle>(null);
  const contactFormV2Ref = useRef<ContactFormModalv2Handle>(null);
  const contactFormV3Ref = useRef<ContactFormModalv3Handle>(null);
  const callMeBackModalRef = useRef<CallMeBackModalHandle>(null);
  const reserveCarModalRef = useRef<ReserveCarModalHandle>(null);
  const reserveCarInfoModalRef = useRef<ReserveCarInfoModalHandle>(null);
  const [isReserved, setIsReserved] = useState(false);

  const {
    external: {
      isLoggedIn,
      newContactFormV2,
      newContactFormV3,
      eidAuth,
      reservationEnabled,
      isItemReserved,
      openContactForm,
      prefilledContactMessage,
      contactTopic,
      isPreview,
      callMeBackEnabled,
    },
  } = useValidDealerItemContext();

  const { trackViewEIDModal } = useTracking();

  const openLoginModal = () => {
    loginModalRef.current?.open();
  };

  /**
   * Opens the appropriate modal in the contact-dealer flow:
   * 1. eID verification modal if required
   * 2. The correct contact form variant
   */
  const openContactFlow = useCallback(
    (options?: OpenContactFlowOptions) => {
      const { prefilledMessage, contactTopic } = options ?? {};

      if (eidAuth.userMustVerify) {
        trackViewEIDModal();
        // eID takes the user off the page, so only the topic survives — as a query parameter
        eidModalRef.current?.open("sendMessage", contactTopic);
        return;
      }

      // Open the appropriate contact form variant
      if (newContactFormV3 && isValidContactFormVariant(newContactFormV3)) {
        if (newContactFormV3 === "v3") {
          contactFormV3Ref.current?.open(prefilledMessage);
        } else if (newContactFormV3 === "v2") {
          contactFormV2Ref.current?.open(prefilledMessage);
        } else {
          contactFormV1Ref.current?.open(prefilledMessage);
        }
      } else if (newContactFormV2) {
        contactFormV2Ref.current?.open(prefilledMessage);
      } else {
        contactFormV1Ref.current?.open(prefilledMessage);
      }
    },
    [eidAuth, newContactFormV2, newContactFormV3, trackViewEIDModal],
  );

  // Auto-open contact form when ?contact query parameter is present.
  useEffect(() => {
    if (!isPreview && openContactForm) {
      // The topic itself comes from `external`, already parsed and validated on the server — this
      // only clears the parameter so a reload does not reopen the form
      const url = new URL(window.location.href);
      url.searchParams.delete("contact");
      window.history.replaceState({}, document.title, url.toString());
      openContactFlow({ prefilledMessage: prefilledContactMessage, contactTopic });
    }
  }, [openContactForm, isPreview, openContactFlow, prefilledContactMessage, contactTopic]);

  /**
   * Same gating as {@link openContactFlow} — the call me back form posts to the same contact
   * endpoint, which requires both a logged in user and a verified eID.
   */
  const openCallMeBackFlow = useCallback(() => {
    if (!isLoggedIn) {
      const { origin, pathname } = window.location;
      const loginUrl = new URL(`${origin}/auth/login`);
      loginUrl.searchParams.set("redirectUrl", `${origin}${pathname}`);
      loginUrl.searchParams.set("cancelUrl", `${origin}${pathname}`);
      window.location.href = loginUrl.toString();
      return;
    }

    if (eidAuth.userMustVerify) {
      trackViewEIDModal();
      eidModalRef.current?.open("callMeBack");
      return;
    }

    callMeBackModalRef.current?.open();
  }, [eidAuth, isLoggedIn, trackViewEIDModal]);

  const openReserveCarModal = () => {
    reserveCarModalRef.current?.open();
  };

  const openReserveCarInfoModal = () => {
    reserveCarInfoModalRef.current?.open();
  };

  const resolveContactFormModal = () => {
    if (newContactFormV3 && isValidContactFormVariant(newContactFormV3)) {
      const modals: Record<string, ReactNode> = {
        v1: <ContactFormModal ref={contactFormV1Ref} />,
        v2: <ContactFormModalv2 ref={contactFormV2Ref} />,
        v3: <ContactFormModalv3 ref={contactFormV3Ref} />,
      };
      return modals[newContactFormV3];
    }
    return newContactFormV2 ? (
      <ContactFormModalv2 ref={contactFormV2Ref} />
    ) : (
      <ContactFormModal ref={contactFormV1Ref} />
    );
  };

  return (
    <ModalContext
      value={{
        openLoginModal,
        openContactFlow,
        openCallMeBackFlow,
        openReserveCarModal,
        openReserveCarInfoModal,
        isReserved: isReserved || isItemReserved,
        setIsReserved,
      }}
    >
      {isLoggedIn && eidAuth.userMustVerify && <EidModal ref={eidModalRef} />}
      {isLoggedIn && !eidAuth.userMustVerify && resolveContactFormModal()}
      {isLoggedIn && !eidAuth.userMustVerify && callMeBackEnabled && <CallMeBackModal ref={callMeBackModalRef} />}

      {!isLoggedIn && reservationEnabled && !isItemReserved && <LoginModal ref={loginModalRef} />}
      {reservationEnabled && !isItemReserved && (
        <ReserveCarModal ref={reserveCarModalRef} setIsReserved={setIsReserved} />
      )}
      <ReserveCarInfoModal ref={reserveCarInfoModalRef} />
      {children}
    </ModalContext>
  );
}

export function useModals() {
  const context = useContext(ModalContext);
  if (typeof context === "undefined") {
    throw new Error("useModals must be used within a ModalProvider");
  }
  return context;
}
